import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { meetsMinLevel } from '../lib/permissions'

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.requireSettingsAccess] — admin or super_admin
 * @param {string} [props.requiredPageKey] — RBAC page key; requires canAccessPage
 * @param {string[]} [props.requiredPageKeyAnyOf] — grant access if user can access any listed page key
 * @param {'read'|'write'} [props.minAccess] — default read; with requiredPageKey, enforces pageAccessLevel
 * @deprecated Use requireSettingsAccess + requiredPageKey instead of requireAdmin
 * @param {boolean} [props.requireAdmin] — legacy: super_admin only (for gradual migration)
 */
function ProtectedRoute({
  children,
  requireSettingsAccess = false,
  requiredPageKey = null,
  requiredPageKeyAnyOf = null,
  minAccess = 'read',
  requireAdmin = false,
}) {
  const { user, profile, loading, canOpenSettings, canAccessPage, pageAccessLevel } = useAuth()

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#111827',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ color: '#9ca3af', fontSize: '16px' }}>Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requireAdmin && profile?.role !== 'super_admin') {
    return <Navigate to="/" replace />
  }

  if (requireSettingsAccess && !canOpenSettings) {
    return <Navigate to="/" replace />
  }

  if (requiredPageKeyAnyOf?.length) {
    const allowedKey = requiredPageKeyAnyOf.find((key) => canAccessPage(key))
    if (!allowedKey) {
      return <Navigate to="/" replace />
    }
    const level = pageAccessLevel(allowedKey)
    if (!meetsMinLevel(minAccess, level)) {
      return <Navigate to="/" replace />
    }
  } else if (requiredPageKey) {
    if (!canAccessPage(requiredPageKey)) {
      return <Navigate to="/" replace />
    }
    const level = pageAccessLevel(requiredPageKey)
    if (!meetsMinLevel(minAccess, level)) {
      return <Navigate to="/" replace />
    }
  }

  return children
}

export default ProtectedRoute
